import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import * as CompanyService from '@/services/accountService/companyService';
import type { CompanyInfo } from '@/services/accountService/companyService';
import { normalizeIndustryIds } from '@/services/accountService/companyService';
import CompanyDetail from '@/components/company-info/company/CompanyDetail';
import { useGlInvoiceCreateCtx } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { matchCompanyFromImportName } from '@/utils/glImportHelpers';

const PANEL_HEIGHT = 'calc(100vh - 300px)';

export default function StepACompany() {
    const { state, dispatch, crValidateRef } = useGlInvoiceCreateCtx();
    const hasImportedContent = !!state.contentData;

    const [companies, setCompanies] = useState<CompanyInfo[]>([]);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [companyError, setCompanyError] = useState<string | null>(null);
    const [companyInput, setCompanyInput] = useState('');
    const companyIdleRef = useRef<any>(null);
    const [showCreateCompany, setShowCreateCompany] = useState(false);
    const [inlineCreateOpen, setInlineCreateOpen] = useState(false);
    const saveRef = useRef<(() => Promise<boolean>) | null>(null);

    const selectedCompany = state.selectedCompany;

    // Excel 导入后后端匹配到已有公司 → display 模式（参照 StepACo）
    const isDisplayMode = !!(state.importFlags?.companyMatched && state.importedCompany);

    useEffect(() => {
        setShowCreateCompany(!selectedCompany && !inlineCreateOpen);
    }, [selectedCompany, inlineCreateOpen]);

    useEffect(() => {
        if (selectedCompany) setInlineCreateOpen(false);
    }, [selectedCompany]);

    // §8.1: Auto-react to Company Info import — 后端匹配或按公司名兜底，否则才打开创建表单
    const contentAppliedRef = useRef(false);
    useEffect(() => {
        if (!state.contentData || contentAppliedRef.current) return;
        if (state.importFlags === null) return;

        if (state.importFlags?.companyMatched && (state.importedCompany || selectedCompany)) {
            contentAppliedRef.current = true;
            setInlineCreateOpen(false);
            return;
        }

        if (selectedCompany) {
            contentAppliedRef.current = true;
            return;
        }

        // 有公司名时等公司列表加载完再决定（避免 ADEL 等先闪创建表单）
        if (state.contentData.companyName && companyLoading) return;

        const matched = matchCompanyFromImportName(companies, state.contentData.companyName);
        if (matched) {
            contentAppliedRef.current = true;
            dispatch({ type: 'set_company', payload: matched });
            dispatch({ type: 'set_imported_company', payload: matched });
            dispatch({
                type: 'set_import_flags',
                payload: { companyMatched: true, companyNameUpdated: false },
            });
            setInlineCreateOpen(false);
            setCompanyInput((matched as any).companyName || '');
            return;
        }

        contentAppliedRef.current = true;
        if (!state.importFlags?.companyMatched) {
            setInlineCreateOpen(true);
        }
    }, [
        state.contentData,
        state.importFlags,
        state.importedCompany,
        selectedCompany,
        companies,
        companyLoading,
        dispatch,
    ]);

    useEffect(() => {
        if (selectedCompany?.companyName) {
            setCompanyInput(selectedCompany.companyName);
        }
    }, [selectedCompany]);

    useEffect(() => {
        const run = async () => {
            setCompanyLoading(true);
            setCompanyError(null);
            try {
                const list = await CompanyService.querySelectionList();
                setCompanies(list || []);
            } catch (e: any) {
                setCompanyError(e?.message || 'Failed to load companies');
            } finally {
                setCompanyLoading(false);
            }
        };
        run();
    }, []);

    const reloadAndSelectNewest = useCallback(async () => {
        try {
            setCompanyLoading(true);
            const list = await CompanyService.querySelectionList();
            const options = Array.isArray(list) ? list : [];
            setCompanies(options as any);
            const newest = options.reduce((acc: any, cur: any) =>
                acc == null || (cur.companyId ?? cur.id) > (acc.companyId ?? acc.id) ? cur : acc, null);
            if (newest) dispatch({ type: 'set_company', payload: newest });
        } finally {
            setCompanyLoading(false);
        }
    }, [dispatch]);

    // 注册 beforeNext 回调，父组件 GlCreateStepper 的 Next 按钮触发 CompanyDetail 保存
    useEffect(() => {
        if (inlineCreateOpen || selectedCompany) {
            dispatch({
                type: 'set_before_next',
                payload: async () => {
                    if (!saveRef.current) return true;
                    const ok = await saveRef.current();
                    if (!ok) return false;
                    // 创建模式：保存成功后刷新公司列表并选中最新公司
                    if (inlineCreateOpen) {
                        await reloadAndSelectNewest();
                    }
                    return true;
                }
            });
        } else {
            dispatch({ type: 'set_before_next', payload: undefined });
        }
        return () => {
            dispatch({ type: 'set_before_next', payload: undefined });
        };
    }, [inlineCreateOpen, selectedCompany, dispatch, reloadAndSelectNewest]);

    return (
        <Box sx={{ height: PANEL_HEIGHT, overflow: 'auto' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>Select Company</Typography>
            {companyError && <Alert severity="error" sx={{ mb: 1 }}>{companyError}</Alert>}
            <Autocomplete
                value={selectedCompany}
                disabled={inlineCreateOpen || isDisplayMode}
                onChange={(_, v) => dispatch({ type: 'set_company', payload: v as any })}
                options={companies}
                loading={companyLoading}
                getOptionLabel={(o) => o?.companyName || ''}
                isOptionEqualToValue={(opt, val) => {
                    const ok = (opt as any) || {};
                    const vk = (val as any) || {};
                    return (ok.companyId ?? ok.id) === (vk.companyId ?? vk.id);
                }}
                renderOption={(props, option) => {
                    const { key: _key, ...optionProps } = props;
                    const id = (option as any).companyId ?? (option as any).id;
                    return (
                        <li {...optionProps} key={id ?? _key}>
                            {(option as any).companyName}
                        </li>
                    );
                }}
                inputValue={companyInput}
                onInputChange={(_, v) => {
                    setCompanyInput(v);
                    if (!v || v.trim() === '') {
                        setShowCreateCompany(!selectedCompany && !inlineCreateOpen);
                        if (companyIdleRef.current) clearTimeout(companyIdleRef.current);
                        return;
                    }
                    setShowCreateCompany(false);
                    if (companyIdleRef.current) clearTimeout(companyIdleRef.current);
                    companyIdleRef.current = setTimeout(() => {
                        setShowCreateCompany(!selectedCompany && !inlineCreateOpen);
                    }, 1200);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Company"
                        placeholder="Select a company"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {companyLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                    {showCreateCompany && (
                                        <Button size="small" sx={{ ml: 0.5 }}
                                            onClick={(e) => { e.stopPropagation(); setInlineCreateOpen(true); }}>
                                            Create Company
                                        </Button>
                                    )}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {inlineCreateOpen && (
                <Box mt={2}>
                    <CompanyDetail
                        open
                        renderMode="content"
                        disableRegisteredOfficeValidation
                        hideRegisteredOffice
                        hideActionButtons
                        saveRef={saveRef}
                        crValidateRef={hasImportedContent ? crValidateRef : undefined}
                        initialData={state.contentData ? {
                            companyName: state.contentData.companyName,
                            industry: normalizeIndustryIds(state.contentData.industry),
                            inCorpDate: state.contentData.inCorpDate,
                            registeredOffice: state.contentData.registeredOffice,
                            br: state.contentData.br,
                        } : undefined}
                        onClose={async (payload) => {
                            setInlineCreateOpen(false);
                            if (payload?.refreshed) await reloadAndSelectNewest();
                        }}
                    />
                </Box>
            )}

            {selectedCompany && !inlineCreateOpen && (
                <Box mt={2}>
                    <CompanyDetail
                        open
                        renderMode="content"
                        companyId={selectedCompany.companyId ?? selectedCompany.id}
                        disableRegisteredOfficeValidation
                        hideRegisteredOffice
                        hideActionButtons
                        saveRef={saveRef}
                        crValidateRef={hasImportedContent ? crValidateRef : undefined}
                        initialData={state.contentData ? {
                            companyName: state.contentData.companyName,
                            industry: normalizeIndustryIds(state.contentData.industry),
                            inCorpDate: state.contentData.inCorpDate,
                            registeredOffice: state.contentData.registeredOffice,
                            br: state.contentData.br,
                        } : undefined}
                        onClose={async (payload) => {
                            if (payload?.refreshed) await reloadAndSelectNewest();
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
